import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Modal } from '@/components/ui';
import { format, parseISO } from 'date-fns';
import { FilterableDataTable } from './FilterableDataTable';
import type { FilterableColumn } from './FilterableDataTable';
import { useEventArpLog, useEventLog } from '../hooks/useEventLog';
import type { EventArpLogRow, EventLogRow } from '../types';

interface EventsLogPanelProps {
  searchText: string;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'M/d/yyyy h:mm a');
  } catch {
    return iso;
  }
}

const eventArpColumns: FilterableColumn<EventArpLogRow>[] = [
  { key: 'eventCode', header: 'Event Code' },
  { key: 'messageType', header: 'Message Type' },
  {
    key: 'logCreatedDate',
    header: 'Log Created Date',
    accessor: (r) => (r.logCreatedDate ? new Date(r.logCreatedDate) : null),
    render: (r) => formatDateTime(r.logCreatedDate),
  },
];

export function EventsLogPanel({ searchText }: EventsLogPanelProps) {
  const { data: rows = [], isLoading, isError } = useEventLog(searchText);
  const [arpEventCode, setArpEventCode] = useState<string | null>(null);
  const arpQuery = useEventArpLog(arpEventCode);

  const columns: FilterableColumn<EventLogRow>[] = [
    { key: 'accountCode', header: 'Account Code' },
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <Link
          to={`/event-details/${encodeURIComponent(row.eventCode)}/${encodeURIComponent(row.accountCode)}`}
          className="text-dds-blue underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-black"
        >
          {row.name}
        </Link>
      ),
    },
    { key: 'eventCode', header: 'Event Code' },
    { key: 'status', header: 'Status' },
    {
      key: 'startDateTime',
      header: 'Start Date',
      accessor: (r) => (r.startDateTime ? new Date(r.startDateTime) : null),
      render: (r) => formatDateTime(r.startDateTime),
    },
    {
      key: 'endDateTime',
      header: 'End Date',
      accessor: (r) => (r.endDateTime ? new Date(r.endDateTime) : null),
      render: (r) => formatDateTime(r.endDateTime),
    },
    {
      key: 'arpLog',
      header: 'ARP Log',
      sortable: false,
      filterable: false,
      align: 'center',
      render: (row) => (
        <button
          type="button"
          onClick={() => setArpEventCode(row.eventCode)}
          aria-label={`Open ARP log for event ${row.eventCode}`}
          className="inline-flex items-center justify-center rounded p-1 text-cool-gray-11 hover:bg-gray-100 hover:text-black focus:outline-none focus:ring-2 focus:ring-black"
        >
          <ExternalLink size={16} aria-hidden="true" />
        </button>
      ),
    },
  ];

  return (
    <section aria-labelledby="event-log-heading" className="space-y-3">
      <div>
        <h2 id="event-log-heading" className="text-lg font-semibold leading-7 text-black">
          Events Log
        </h2>
        {searchText ? (
          <p className="text-sm leading-6 text-cool-gray-11">Filtered by {searchText}</p>
        ) : null}
      </div>

      {isError ? (
        <div role="alert" className="border border-dds-red bg-red-50 p-3 text-sm text-red-700">
          Failed to load events. Please try again.
        </div>
      ) : null}

      <FilterableDataTable
        data={rows}
        columns={columns}
        keyExtractor={(r) => `${r.accountCode}|${r.eventCode}`}
        searchPlaceholder="Search by EventLog..."
        ariaLabel="Events log"
        isLoading={isLoading}
        emptyMessage="No events found."
      />

      <Modal
        isOpen={arpEventCode !== null}
        onClose={() => setArpEventCode(null)}
        title="ARP Log"
        className="max-w-4xl"
      >
        {arpQuery.isLoading ? (
          <p className="text-sm text-cool-gray-11">Loading…</p>
        ) : arpQuery.isError ? (
          <p role="alert" className="text-sm text-red-700">
            Failed to load ARP signals.
          </p>
        ) : (
          <FilterableDataTable
            data={arpQuery.data ?? []}
            columns={eventArpColumns}
            keyExtractor={(r) => `${r.eventCode}|${r.messageType}|${r.logCreatedDate}`}
            ariaLabel="Event ARP log"
            bodyMaxHeightClass="max-h-[500px]"
            emptyMessage="No ARP signals found for this event."
          />
        )}
      </Modal>
    </section>
  );
}
